import { useState, useEffect } from "react";
import {
  User,
  Plus,
  Send,
  Lock,
  Bell,
  Trash2,
  Loader2,
  CheckCircle,
  LogOut,
  Mail,
  Phone,
  Edit3,
  Shield,
  Users,
  ChevronRight,
  X,
  Save,
  AlertCircle,
  Settings as SettingsIcon,
  ArrowRight,
  Palette,
  Sun,
  Moon,
  Clock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../components/Layout/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../theme/ThemeContext";
import {
  checkAdmin,
  getMasjidAdmins,
  addMasjidAdmin,
  removeMasjidAdmin,
  sendMasjidNotification,
  changePassword,
  deleteAccount,
} from "../services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { auth } from "../config/firebase";

const API_BASE_URL = import.meta.env.VITE_API_URL;

export default function Settings() {
  const { user, userProfile, logout, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const { themeMode, setThemeMode } = useTheme();

  const [adminInfo, setAdminInfo] = useState(null);
  const [admins, setAdmins] = useState({ owners: [], admins: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState("");

  // Email editing state
  const [editEmailDialogOpen, setEditEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Phone editing state
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  // Admin management
  const [addAdminDialogOpen, setAddAdminDialogOpen] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminRole, setNewAdminRole] = useState("Admin");
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [adminSuccess, setAdminSuccess] = useState(null);

  // Notification
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationBody, setNotificationBody] = useState("");
  const [sendingNotification, setSendingNotification] = useState(false);

  // Account security
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Initialize profile fields
  useEffect(() => {
    if (userProfile) {
      setFirstName(userProfile.firstName || "");
      setLastName(userProfile.lastName || "");
      setPhoneNumber(userProfile.phone || "");
    }
  }, [userProfile]);

  // Load admin info
  useEffect(() => {
    const loadData = async () => {
      if (!user?.email) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const adminData = await checkAdmin(user.email);
        if (adminData?.isAdmin) {
        setAdminInfo(adminData);

        const masjidId =
          adminData.role === "super"
            ? adminData.activeMasjidId
            : adminData.masjidId;

          if (masjidId && (adminData.role === "Admin Owner" || adminData.role === "super")) {
          await loadAdmins(masjidId);
          }
        } else {
          setAdminInfo(null);
        }
      } catch (err) {
        console.error("Error loading data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  const loadAdmins = async (masjidId) => {
    try {
      const result = await getMasjidAdmins(masjidId);
      if (result.ok) {
        setAdmins({
          owners: result.owners || [],
          admins: result.admins || [],
        });
      }
    } catch (err) {
      console.error("Error loading admins:", err);
    }
  };

  // Profile update handler
  const handleUpdateProfile = async () => {
    const cleanFirst = firstName.trim();
    const cleanLast = lastName.trim();

    if (!cleanFirst || !cleanLast) {
      setProfileError("First name and last name are required");
      return;
    }

    if (cleanFirst.length < 2 || cleanLast.length < 2) {
      setProfileError("Names must be at least 2 characters");
      return;
    }

    try {
      setProfileSaving(true);
      setProfileError("");

      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(`${API_BASE_URL}/v1/account/profile`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ firstName: cleanFirst, lastName: cleanLast }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to update profile");
      }

      setEditingProfile(false);
      setSuccess("Profile updated successfully");
      setTimeout(() => setSuccess(null), 3000);
      
      if (refreshUserProfile) {
        await refreshUserProfile();
      }
    } catch (err) {
      setProfileError(err.message || "Failed to update profile");
    } finally {
      setProfileSaving(false);
    }
  };

  // Email update handler
  const handleUpdateEmail = async () => {
    const cleanEmail = newEmail.trim().toLowerCase();

    if (!cleanEmail || !emailPassword) {
      setEmailError("Email and password are required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      setEmailError("Invalid email format");
      return;
    }

    try {
      setEmailSaving(true);
      setEmailError("");

      // Re-authenticate first
      const credential = EmailAuthProvider.credential(user.email, emailPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);

      const token = await auth.currentUser?.getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/v1/account/email`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ newEmail: cleanEmail, currentPassword: emailPassword }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to update email");
      }

      setEditEmailDialogOpen(false);
      setNewEmail("");
      setEmailPassword("");
      setSuccess("Email updated successfully");
      setTimeout(() => setSuccess(null), 3000);
      
      if (refreshUserProfile) {
        await refreshUserProfile();
      }
    } catch (err) {
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setEmailError("Incorrect password");
      } else {
        setEmailError(err.message || "Failed to update email");
      }
    } finally {
      setEmailSaving(false);
    }
  };

  // Phone update handler
  const handleUpdatePhone = async () => {
    const cleanPhone = phoneNumber.trim();

    try {
      setPhoneSaving(true);
      setPhoneError("");

      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(`${API_BASE_URL}/v1/account/phone`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ phone: cleanPhone }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to update phone");
      }

      setEditingPhone(false);
      setSuccess("Phone number updated successfully");
      setTimeout(() => setSuccess(null), 3000);
      
      if (refreshUserProfile) {
        await refreshUserProfile();
      }
    } catch (err) {
      setPhoneError(err.message || "Failed to update phone");
    } finally {
      setPhoneSaving(false);
    }
  };

  // Add admin
  const handleAddAdmin = async () => {
    const masjidId =
      adminInfo?.role === "super"
        ? adminInfo.activeMasjidId
        : adminInfo?.masjidId;

    if (!masjidId || !newAdminEmail.trim()) return;

    try {
      setAdminSaving(true);
      setAdminError(null);
      setAdminSuccess(null);

      await addMasjidAdmin(masjidId, newAdminEmail.trim(), newAdminRole);

      setAdminSuccess("Admin added successfully!");
      setNewAdminEmail("");
      setNewAdminRole("Admin");
      setAddAdminDialogOpen(false);
      setTimeout(() => setAdminSuccess(null), 3000);

      await loadAdmins(masjidId);
    } catch (err) {
      setAdminError(
        err.response?.data?.error || "Failed to add admin. Please try again."
      );
    } finally {
      setAdminSaving(false);
    }
  };

  // Remove admin
  const handleRemoveAdmin = async (email) => {
    const masjidId =
      adminInfo?.role === "super"
        ? adminInfo.activeMasjidId
        : adminInfo?.masjidId;

    if (!masjidId) return;

    if (!confirm(`Are you sure you want to remove ${email}?`)) return;

    try {
      setAdminError(null);
      await removeMasjidAdmin(masjidId, email);
      setAdminSuccess("Admin removed successfully!");
      setTimeout(() => setAdminSuccess(null), 3000);
      await loadAdmins(masjidId);
    } catch (err) {
      setAdminError(
        err.response?.data?.error || "Failed to remove admin. Please try again."
      );
    }
  };

  // Send notification
  const handleSendNotification = async () => {
    const masjidId =
      adminInfo?.role === "super"
        ? adminInfo.activeMasjidId
        : adminInfo?.masjidId;

    if (!masjidId || !notificationBody.trim()) return;

    if (notificationBody.length > 500) {
      setError("Notification must be 500 characters or less");
      return;
    }

    try {
      setSendingNotification(true);
      setError(null);

      const result = await sendMasjidNotification(masjidId, notificationBody.trim());

      setSuccess(result.message || "Notification sent successfully!");
      setNotificationBody("");
      setNotificationDialogOpen(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to send notification. Please try again."
      );
    } finally {
      setSendingNotification(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      console.error("Error logging out:", err);
      setError("Failed to log out. Please try again.");
    }
  };

  const handleChangePassword = async () => {
    try {
      setPasswordError("");

      if (!currentPassword || !newPassword || !confirmPassword) {
        setPasswordError("All password fields are required.");
        return;
      }

      if (newPassword.length < 6) {
        setPasswordError("New password must be at least 6 characters.");
        return;
      }

      if (newPassword !== confirmPassword) {
        setPasswordError("New passwords do not match.");
        return;
      }

      if (!auth.currentUser?.email) {
        setPasswordError("You must be signed in to update your password.");
        return;
      }

      setPasswordLoading(true);

      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(auth.currentUser, credential);
      await auth.currentUser.getIdToken(true);

      await changePassword(currentPassword, newPassword);

      setPasswordDialogOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSuccess("Password updated successfully.");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || "Failed to update password.";
      setPasswordError(message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setDeleteError("");

      if (!deletePassword) {
        setDeleteError("Password is required to confirm deletion.");
        return;
      }

      if (!auth.currentUser?.email) {
        setDeleteError("You must be signed in to delete your account.");
        return;
      }

      setDeleteLoading(true);

      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        deletePassword
      );
      await reauthenticateWithCredential(auth.currentUser, credential);
      await auth.currentUser.getIdToken(true);

      await deleteAccount();

      setDeleteDialogOpen(false);
      setDeletePassword("");
      await logout();
      navigate("/login");
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || "Failed to delete account.";
      setDeleteError(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const displayName =
    userProfile?.firstName || userProfile?.lastName
      ? `${userProfile?.firstName || ""} ${userProfile?.lastName || ""}`.trim()
      : user?.email?.split("@")[0] || "User";

  const canManageAdmins =
    adminInfo?.role === "Admin Owner" || adminInfo?.role === "super";
  const canSendNotifications = adminInfo?.masjidId || adminInfo?.activeMasjidId;

  const formatTimestamp = (ts) => {
    if (!ts) return "";
    try {
      const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return "";
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl" />
            <Loader2 className="relative h-16 w-16 animate-spin text-primary" />
          </div>
          <p className="text-lg text-muted-foreground">Loading settings...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="relative min-h-screen">
        {/* Background - matching MasjidDashboard and Home */}
        <div className="pointer-events-none fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(191,154,47,0.15),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(191,154,47,0.1),transparent_50%)]" />
          <div className="absolute right-1/4 top-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        </div>

        <div className="mx-auto max-w-4xl space-y-6 px-4 py-6 sm:space-y-8 sm:px-6 sm:py-8">
          
          {/* Header Card - matching MasjidDashboard style */}
          <Card className="relative overflow-hidden border-none bg-gradient-to-r from-card via-card to-card/80 shadow-xl">
            <div className="absolute -right-20 -top-20 hidden h-64 w-64 rounded-full bg-primary/10 blur-3xl sm:block" />
            
            <CardContent className="relative p-4 sm:p-6 lg:p-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/20 sm:h-14 sm:w-14">
                    <SettingsIcon className="h-6 w-6 text-primary sm:h-7 sm:w-7" />
                  </div>
                  <div className="min-w-0">
                    <h1 className="truncate text-xl font-bold tracking-tight sm:text-2xl lg:text-3xl">
                      Settings
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Manage your account and preferences
                    </p>
                  </div>
                </div>
                
                <Badge
                  className="w-fit shrink-0 border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs font-medium tracking-wide text-primary sm:px-4 sm:py-2 sm:text-sm"
                >
                  <User className="mr-1.5 h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  {adminInfo?.role || "User"}
                </Badge>
                  </div>
            </CardContent>
          </Card>

          {/* Success/Error Messages */}
          {success && (
            <div className="flex items-center gap-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-4 shadow-lg">
              <CheckCircle className="h-5 w-5 text-emerald-500 shrink-0" />
              <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">{success}</p>
                </div>
          )}

          {error && (
            <div className="flex items-center gap-3 rounded-xl bg-destructive/10 border border-destructive/20 p-4 shadow-lg">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
              <p className="text-sm font-medium text-destructive">{error}</p>
                </div>
          )}

          {/* Profile Card */}
          <Card className="group relative overflow-hidden border-none bg-gradient-to-br from-primary/20 via-primary/10 to-transparent shadow-lg transition-all duration-300">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20 transition-transform group-hover:scale-105 sm:h-14 sm:w-14">
                    <User className="h-6 w-6 sm:h-7 sm:w-7" />
                  </div>
                  <div>
                    <CardTitle className="text-lg sm:text-xl">Profile</CardTitle>
                    <CardDescription>Your personal information</CardDescription>
                  </div>
                </div>
                {!editingProfile && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingProfile(true)}
                    className="gap-2 border-primary/20 hover:bg-primary/10 hover:text-primary"
                  >
                    <Edit3 className="h-4 w-4" />
                    <span className="hidden sm:inline">Edit</span>
                  </Button>
                )}
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {editingProfile ? (
              <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Enter first name"
                        className="border-primary/20 focus-visible:ring-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Enter last name"
                        className="border-primary/20 focus-visible:ring-primary"
                      />
                    </div>
                  </div>
                  {profileError && (
                    <p className="text-sm text-destructive">{profileError}</p>
                  )}
                  <div className="flex gap-2">
                  <Button
                      onClick={handleUpdateProfile}
                      disabled={profileSaving}
                      className="gap-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
                  >
                      {profileSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Save Changes
                  </Button>
                  <Button
                      variant="outline"
                      onClick={() => {
                        setEditingProfile(false);
                        setFirstName(userProfile?.firstName || "");
                        setLastName(userProfile?.lastName || "");
                        setProfileError("");
                      }}
                    >
                      Cancel
                  </Button>
                  </div>
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl bg-background/50 p-4 ring-1 ring-border/50">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Full Name</p>
                    <p className="font-semibold text-foreground">{displayName}</p>
                  </div>
                  <div className="rounded-xl bg-background/50 p-4 ring-1 ring-border/50">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Account Type</p>
                    <Badge variant="secondary" className="mt-0.5 bg-primary/10 text-primary border-0">
                      {adminInfo?.role || "User"}
                    </Badge>
                  </div>
                </div>
              )}

              {/* Email Row */}
              <div className="flex items-center justify-between rounded-xl bg-background/50 p-4 ring-1 ring-border/50 transition-colors hover:bg-background/70">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-500/10">
                    <Mail className="h-5 w-5 text-sky-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-muted-foreground">Email Address</p>
                    <p className="font-medium truncate">{user?.email}</p>
                  </div>
                </div>
                  <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditEmailDialogOpen(true)}
                  className="shrink-0 h-9 w-9 hover:bg-sky-500/10 hover:text-sky-500"
                  >
                  <Edit3 className="h-4 w-4" />
                  </Button>
                </div>

              {/* Phone Row */}
              <div className="flex items-center justify-between rounded-xl bg-background/50 p-4 ring-1 ring-border/50 transition-colors hover:bg-background/70">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10">
                    <Phone className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-muted-foreground">Phone Number</p>
                    {editingPhone ? (
                      <Input
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="Enter phone number"
                        className="mt-1 h-8 border-emerald-500/20 focus-visible:ring-emerald-500"
                      />
                    ) : (
                      <p className="font-medium truncate">
                        {userProfile?.phone || "Not provided"}
                      </p>
                )}
              </div>
                    </div>
                {editingPhone ? (
                  <div className="flex gap-1 shrink-0 ml-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleUpdatePhone}
                      disabled={phoneSaving}
                      className="h-9 w-9 hover:bg-emerald-500/10 hover:text-emerald-500"
                    >
                      {phoneSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingPhone(false);
                        setPhoneNumber(userProfile?.phone || "");
                        setPhoneError("");
                      }}
                      className="h-9 w-9"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                            </div>
                ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                    onClick={() => setEditingPhone(true)}
                    className="shrink-0 h-9 w-9 hover:bg-emerald-500/10 hover:text-emerald-500"
                              >
                    <Edit3 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
              {phoneError && (
                <p className="text-sm text-destructive">{phoneError}</p>
              )}
            </CardContent>
          </Card>

          {/* Theme Settings Card */}
          <Card className="group relative overflow-hidden border-none bg-gradient-to-br from-purple-500/20 via-purple-500/10 to-transparent shadow-lg transition-all duration-300">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-purple-500/10 text-purple-500 ring-1 ring-purple-500/20 transition-transform group-hover:scale-105 sm:h-14 sm:w-14">
                  <Palette className="h-6 w-6 sm:h-7 sm:w-7" />
                </div>
                <div>
                  <CardTitle className="text-lg sm:text-xl">Appearance</CardTitle>
                  <CardDescription>Customize your theme preferences</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Theme Mode Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Theme Mode</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: 'light', label: 'Light', icon: Sun },
                    { key: 'dark', label: 'Dark', icon: Moon },
                    { key: 'system', label: 'System', icon: Clock },
                  ].map(({ key, label, icon: Icon }) => {
                    const isSelected = themeMode === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setThemeMode(key)}
                        className={`flex flex-col items-center gap-2 rounded-xl p-4 ring-1 transition-all active:scale-[0.98] ${
                          isSelected
                            ? 'ring-2 ring-primary bg-primary/5 shadow-md'
                            : 'ring-border/50 bg-background/50 hover:bg-background/70 hover:ring-border'
                        }`}
                      >
                        <Icon className={`h-6 w-6 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                        <span className={`text-sm font-semibold ${isSelected ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {label}
                        </span>
                        {isSelected && (
                          <CheckCircle className="h-4 w-4 text-primary" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Masjid Admin Management - Only show for admins */}
          {adminInfo?.isAdmin && (
            <Card className="group relative overflow-hidden border-none bg-gradient-to-br from-indigo-500/20 via-indigo-500/10 to-transparent shadow-lg transition-all duration-300">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-500/10 text-indigo-500 ring-1 ring-indigo-500/20 transition-transform group-hover:scale-105 sm:h-14 sm:w-14">
                    <Users className="h-6 w-6 sm:h-7 sm:w-7" />
                  </div>
                  <div>
                    <CardTitle className="text-lg sm:text-xl">Masjid Management</CardTitle>
                    <CardDescription>Manage your masjid administrators</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <button
                  onClick={() => setAddAdminDialogOpen(true)}
                  className="flex w-full items-center justify-between rounded-xl bg-background/50 p-4 text-left ring-1 ring-border/50 transition-all hover:bg-background/70 hover:ring-indigo-500/30 active:scale-[0.99]"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/10">
                      <Users className="h-5 w-5 text-indigo-500" />
                    </div>
                    <div>
                      <p className="font-semibold">Manage Admins</p>
                      <p className="text-sm text-muted-foreground">Add or remove administrators</p>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground" />
                </button>

                <button
                  onClick={() => setNotificationDialogOpen(true)}
                  className="flex w-full items-center justify-between rounded-xl bg-background/50 p-4 text-left ring-1 ring-border/50 transition-all hover:bg-background/70 hover:ring-indigo-500/30 active:scale-[0.99]"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
                      <Bell className="h-5 w-5 text-purple-500" />
                    </div>
                    <div>
                      <p className="font-semibold">Send Notification</p>
                      <p className="text-sm text-muted-foreground">Notify all subscribers</p>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground" />
                </button>
              </CardContent>
            </Card>
          )}


          {/* Security Card */}
          <Card className="group relative overflow-hidden border-none bg-gradient-to-br from-slate-500/20 via-slate-500/10 to-transparent shadow-lg transition-all duration-300">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-500/10 text-slate-500 ring-1 ring-slate-500/20 transition-transform group-hover:scale-105 sm:h-14 sm:w-14">
                  <Lock className="h-6 w-6 sm:h-7 sm:w-7" />
                </div>
                  <div>
                  <CardTitle className="text-lg sm:text-xl">Security</CardTitle>
                  <CardDescription>Manage your account security</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <button
                onClick={() => setPasswordDialogOpen(true)}
                className="flex w-full items-center justify-between rounded-xl bg-background/50 p-4 text-left ring-1 ring-border/50 transition-all hover:bg-background/70 hover:ring-slate-500/30 active:scale-[0.99]"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
                    <Lock className="h-5 w-5 text-blue-500" />
                  </div>
                            <div>
                    <p className="font-semibold">Change Password</p>
                    <p className="text-sm text-muted-foreground">Update your password</p>
                            </div>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
              </button>

              <button
                onClick={handleLogout}
                className="flex w-full items-center justify-between rounded-xl bg-background/50 p-4 text-left ring-1 ring-border/50 transition-all hover:bg-background/70 hover:ring-slate-500/30 active:scale-[0.99]"
                              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10">
                    <LogOut className="h-5 w-5 text-orange-500" />
                          </div>
                  <div>
                    <p className="font-semibold">Log Out</p>
                    <p className="text-sm text-muted-foreground">Sign out of your account</p>
                      </div>
                  </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
              </button>
                </CardContent>
              </Card>

          {/* Danger Zone */}
          <Card className="group relative overflow-hidden border-none bg-gradient-to-br from-destructive/20 via-destructive/10 to-transparent shadow-lg transition-all duration-300">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive ring-1 ring-destructive/20 transition-transform group-hover:scale-105 sm:h-14 sm:w-14">
                  <Trash2 className="h-6 w-6 sm:h-7 sm:w-7" />
                </div>
                <CardTitle className="text-lg sm:text-xl text-destructive">Danger Zone</CardTitle>
              </div>
                </CardHeader>
            <CardContent>
              <button
                onClick={() => setDeleteDialogOpen(true)}
                className="flex w-full items-center justify-between rounded-xl bg-destructive/5 p-4 text-left ring-1 ring-destructive/20 transition-all hover:bg-destructive/10 hover:ring-destructive/40 active:scale-[0.99]"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10">
                    <Trash2 className="h-5 w-5 text-destructive" />
                  </div>
                  <div>
                    <p className="font-semibold text-destructive">Delete Account</p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-destructive/70" />
              </button>
            </CardContent>
          </Card>
        </div>

        {/* Edit Email Dialog */}
        <Dialog open={editEmailDialogOpen} onOpenChange={setEditEmailDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Change Email Address</DialogTitle>
              <DialogDescription>
                Enter your new email and current password to verify.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
                  <div className="space-y-2">
                <Label htmlFor="new-email">New Email</Label>
                <Input
                  id="new-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email address"
                  className="border-primary/20 focus-visible:ring-primary"
                />
                  </div>
              <div className="space-y-2">
                <Label htmlFor="email-password">Current Password</Label>
                <Input
                  id="email-password"
                  type="password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  placeholder="Enter password to confirm"
                  className="border-primary/20 focus-visible:ring-primary"
                />
              </div>
              {emailError && (
                <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
                  {emailError}
                </div>
              )}
            </div>
            <DialogFooter>
                  <Button
                variant="outline"
                onClick={() => {
                  setEditEmailDialogOpen(false);
                  setNewEmail("");
                  setEmailPassword("");
                  setEmailError("");
                }}
              >
                Cancel
                  </Button>
              <Button 
                onClick={handleUpdateEmail} 
                disabled={emailSaving}
                className="bg-gradient-to-r from-primary to-primary/80"
              >
                {emailSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Update Email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
              <DialogDescription>
                Enter your current password and choose a new one.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Current Password</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="border-primary/20 focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="border-primary/20 focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  className="border-primary/20 focus-visible:ring-primary"
                />
              </div>
              {passwordError && (
                <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
                  {passwordError}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setPasswordDialogOpen(false);
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                  setPasswordError("");
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleChangePassword} 
                disabled={passwordLoading}
                className="bg-gradient-to-r from-primary to-primary/80"
              >
                {passwordLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Update Password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Account Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-destructive">Delete Account</DialogTitle>
              <DialogDescription>
                This action is permanent and cannot be undone. All your data will be deleted forever.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-4">
                <p className="text-sm text-destructive font-medium">
                  Warning: This will permanently delete your account, profile, and all associated data.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="delete-password">Confirm Password</Label>
                <Input
                  id="delete-password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter password to confirm deletion"
                  className="border-destructive/20 focus-visible:ring-destructive"
                />
              </div>
              {deleteError && (
                <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
                  {deleteError}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setDeletePassword("");
                  setDeleteError("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
              >
                {deleteLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Delete Account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Admin Dialog */}
        <Dialog open={addAdminDialogOpen} onOpenChange={setAddAdminDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
            <DialogHeader className="shrink-0">
              <DialogTitle>Manage Admins</DialogTitle>
              <DialogDescription>
                Add or remove administrators for your masjid.
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto space-y-6 py-4 min-h-0">
              {/* Add Admin Form */}
              <div className="space-y-3">
                <Label>Add New Admin</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="email"
                      placeholder="Email address"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                      className="pl-9 border-primary/20 focus-visible:ring-primary"
                      disabled={adminSaving}
                />
              </div>
                <Select value={newAdminRole} onValueChange={setNewAdminRole}>
                    <SelectTrigger className="w-28 border-primary/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin</SelectItem>
                    {adminInfo?.role === "super" && (
                        <SelectItem value="Admin Owner">Owner</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                  <Button
                    onClick={handleAddAdmin}
                    disabled={adminSaving || !newAdminEmail.trim()}
                    className="shrink-0 bg-gradient-to-r from-primary to-primary/80"
                  >
                    {adminSaving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                  </Button>
              </div>
                <p className="text-xs text-muted-foreground">
                  User must have an existing account to be added as admin
                </p>
              </div>

              {/* Admin List */}
              {admins.owners.length === 0 && admins.admins.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 gap-3">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="font-medium">No admins yet</p>
                  <p className="text-sm text-muted-foreground text-center">
                    Add admins to help manage your masjid
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Admin Owners */}
                  {admins.owners.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Admin Owners
                        </span>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                      <div className="space-y-2">
                        {admins.owners.map((admin) => (
                          <div
                            key={admin.email}
                            className="flex items-center gap-3 rounded-xl border bg-card p-3 ring-1 ring-border/50"
                          >
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
                              <Shield className="h-5 w-5 text-amber-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="truncate text-sm font-medium">{admin.email}</p>
                                {admin.email === user?.email && (
                                  <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                    You
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Owner • Added {formatTimestamp(admin.createdAt)}
                              </p>
                            </div>
                            {adminInfo?.role === "super" && admin.email !== user?.email && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="shrink-0 h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => handleRemoveAdmin(admin.email)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Regular Admins */}
                  {admins.admins.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Admins
                        </span>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                      <div className="space-y-2">
                        {admins.admins.map((admin) => (
                          <div
                            key={admin.email}
                            className="flex items-center gap-3 rounded-xl border bg-card p-3 ring-1 ring-border/50"
                          >
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-500/10">
                              <Users className="h-5 w-5 text-indigo-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="truncate text-sm font-medium">{admin.email}</p>
                              <p className="text-xs text-muted-foreground">
                                Admin • Added {formatTimestamp(admin.createdAt)}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="shrink-0 h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => handleRemoveAdmin(admin.email)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="shrink-0 pt-4">
              <Button variant="outline" onClick={() => setAddAdminDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send Notification Dialog */}
        <Dialog open={notificationDialogOpen} onOpenChange={setNotificationDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Send Notification</DialogTitle>
              <DialogDescription>
                Send a push notification to all users subscribed to your masjid.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="notification-body">Message</Label>
                <Textarea
                  id="notification-body"
                  rows={4}
                  value={notificationBody}
                  onChange={(e) => setNotificationBody(e.target.value)}
                  placeholder="Enter your notification message (max 500 characters)"
                  className="border-primary/20 focus-visible:ring-primary resize-none"
                />
                <div className="flex justify-between items-center">
                  <p className="text-xs text-muted-foreground">
                    This will be sent to all subscribers
                  </p>
                  <p className={`text-xs font-medium ${notificationBody.length > 500 ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {notificationBody.length}/500
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setNotificationDialogOpen(false);
                  setNotificationBody("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendNotification}
                disabled={sendingNotification || !notificationBody.trim() || notificationBody.length > 500}
                className="bg-gradient-to-r from-primary to-primary/80"
              >
                {sendingNotification ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Send Notification
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}